<template>
  <div class="login-shell">
    <div class="card">
      <h1 class="title">
        Anmeldung
      </h1>
      <p class="subtitle">
        Bitte melden Sie sich mit Ihrer E‑Mail und Ihrem Passwort an.
      </p>

      <form
        class="form"
        @submit.prevent="onSubmit"
      >
        <label
          class="label"
          for="email"
        >E‑Mail</label>
        <input
          id="email"
          v-model.trim="email"
          type="email"
          class="input"
          autocomplete="username"
          required
          placeholder="name@beispiel.de"
        >

        <label
          class="label"
          for="password"
        >Passwort</label>
        <input
          id="password"
          v-model.trim="password"
          type="password"
          class="input"
          autocomplete="current-password"
          required
          placeholder="••••••••"
        >

        <button
          class="btn"
          :disabled="loading"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          />
          <span>{{ loading ? 'Wird geprüft…' : 'Einloggen' }}</span>
        </button>

        <!-- Neuer Link "Passwort vergessen" -->
        <ForgotPasswordLink path="/forgot-password" />

        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
      </form>

      <p
        v-if="success"
        class="notice notice-success mt-12"
        role="status"
      >
        Login erfolgreich.
      </p>
    </div>
    <PublicLegalFooter />
  </div>
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount} from 'vue';
import {login} from '@/stores/auth';
import ForgotPasswordLink from '@/components/auth/ForgotPasswordLink.vue';
import PublicLegalFooter from '@/components/layout/PublicLegalFooter.vue';

onMounted(() => {
  const nav = document.querySelector('.top-nav');
  const el = document.getElementById('top-nav-help-login');
  if (!nav || !el) return;
  const profile = nav.querySelector('.profile');
  if (profile && profile.parentNode === nav) nav.insertBefore(el, profile);
  else nav.appendChild(el);
});

onBeforeUnmount(() => {
  const el = document.getElementById('top-nav-help-login');
  if (el && el.parentNode) el.parentNode.removeChild(el);
});

const email = ref('');
const password = ref('');
const loading = ref(false);
const error = ref(null);
const success = ref(false);

async function onSubmit() {
  error.value = null;
  success.value = false;
  loading.value = true;

  try {
    const res = await login({email: email.value, password: password.value});

    if (res && res.success && res.token && res.user) {
      success.value = true;
      // Persistenz nach Policy: hier simple Demo → LocalStorage
      try {
        localStorage.setItem('auth_token', res.token);
        localStorage.setItem('auth_user', JSON.stringify(res.user));
      } catch (e) {
        /* no-op */
      }
    } else {
      error.value = (res && res.message) ? res.message : 'Ungültige Zugangsdaten';
    }
  } catch (e) {
    const msg =
        (e && e.response && e.response.data && e.response.data.message) ||
        (e && e.message) ||
        'Ungültige Zugangsdaten';
    error.value = msg;
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.login-shell {
  min-height: 100vh;
  display: grid;
  place-items: center;
  background: linear-gradient(180deg, var(--bg) 0%, #EEF1F6 100%);
  padding: 24px;
}

.card {
  background: var(--surface);
  color: var(--text);
  width: 100%;
  max-width: 420px;
  border: 1px solid var(--border-strong);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.08), 0 1px 0 rgba(0, 0, 0, .04);
  padding: 24px 20px;
}

.title {
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  margin: 0 0 6px 0;
}

.subtitle {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  margin: 0 0 16px 0;
}

.form {
  display: grid;
  gap: 14px;
}

.label {
  font-size: 12px;
  color: var(--muted);
}

.input {
  height: 40px;
  padding: 8px 10px;
  border: 1px solid var(--border-strong);
  border-radius: 8px;
  outline: none;
  background: #fff;
  color: var(--text);
  transition: border-color .12s ease, box-shadow .12s ease, background-color .12s ease;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
}

.input::placeholder {
  color: var(--muted);
  opacity: .9;
}

.input:hover {
  border-color: var(--primary-600);
}

.input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus);
}

.btn {
  height: 42px;
  border-radius: 8px;
  background: var(--primary);
  color: #fff;
  border: 1px solid var(--primary);
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color .12s ease, box-shadow .12s ease, transform .02s ease-in;
  box-shadow: 0 1px 0 rgba(0, 0, 0, .04), 0 6px 16px rgba(79, 70, 229, .18);
}

.btn:hover {
  background: var(--primary-600);
}

.btn:active {
  transform: translateY(1px);
}

.btn:disabled {
  opacity: .7;
  cursor: default;
  box-shadow: none;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, .55);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Viewports: Tablets (Portrait/Landscape) und größer,
   Mobile (<768px) → horizontal scrollbar statt Reduktion */
@media (min-width: 1024px) and (max-width: 1199px) {
  .card {
    max-width: 460px;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .card {
    max-width: 440px;
  }
}

@media (max-width: 767px) {
  .login-shell {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .card {
    margin-inline: auto;
  }
}
</style>
